import type { Factura, FacturasParams } from "../services/facturas.api";
import { badgeEstatusFactura } from "../components/EstatusFacturaBadge";
import { formatFechaIso } from "@/shared/format/fecha";

/** Columnas ordenables/filtrables de la tabla de Mis facturas. */
export type FacturaSortKey =
  | "uuid"
  | "emisor"
  | "receptor"
  | "validacion"
  | "timbrado"
  | "emision"
  | "total"
  | "moneda"
  | "estatus"
  | "serie"
  | "folio"
  | "ordenCompra"
  | "tipo"
  | "observaciones"
  | "numDoc";

export type SortDir = "asc" | "desc";

export interface FacturaSort {
  key: FacturaSortKey;
  dir: SortDir;
}

/** Comparación ascendente por la columna dada. */
function comparar(a: Factura, b: Factura, key: FacturaSortKey): number {
  switch (key) {
    case "uuid":
      return a.uuid.localeCompare(b.uuid, "es");
    case "emisor":
      return a.nombreEmisor.localeCompare(b.nombreEmisor, "es");
    case "receptor":
      return a.nombreReceptor.localeCompare(b.nombreReceptor, "es");
    case "validacion":
      return a.fechaValidacion.localeCompare(b.fechaValidacion);
    case "timbrado":
      return (a.fechaTimbrado ?? "").localeCompare(b.fechaTimbrado ?? "");
    case "emision":
      return (a.fechaEmision ?? "").localeCompare(b.fechaEmision ?? "");
    case "total":
      return a.total - b.total;
    case "moneda":
      return a.moneda.localeCompare(b.moneda, "es");
    case "estatus":
      return a.estatus.localeCompare(b.estatus, "es");
    case "serie":
      return (a.serie ?? "").localeCompare(b.serie ?? "", "es");
    case "folio":
      return (a.folio ?? "").localeCompare(b.folio ?? "", "es");
    case "ordenCompra":
      return (a.ordenCompraClave ?? "").localeCompare(b.ordenCompraClave ?? "", "es");
    case "tipo":
      return a.tipoComprobante.localeCompare(b.tipoComprobante, "es");
    case "observaciones":
      return (a.observaciones ?? "").localeCompare(b.observaciones ?? "", "es");
    case "numDoc":
      return a.numeroSolicitud - b.numeroSolicitud;
    default:
      return 0;
  }
}

/** Devuelve una copia ordenada · si `sort` es null conserva el orden original. */
export function ordenarFacturas(items: Factura[], sort: FacturaSort | null): Factura[] {
  if (!sort) return items;
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => factor * comparar(a, b, sort.key));
}

/** Estado siguiente al hacer click en una columna: nueva → asc; misma → alterna asc/desc. */
export function siguienteSort(actual: FacturaSort | null, key: FacturaSortKey): FacturaSort {
  if (actual?.key === key) {
    return { key, dir: actual.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}

// --- Filtro por columna ------------------------------------------------------

export type FacturaFiltros = Partial<Record<FacturaSortKey, string>>;

/** Texto buscable de una celda para el filtro por columna (contains normalizado). */
export function textoColumna(f: Factura, key: FacturaSortKey): string {
  switch (key) {
    case "uuid":
      return f.uuid;
    case "emisor":
      return `${f.nombreEmisor} ${f.rfcEmisor}`;
    case "receptor":
      return `${f.nombreReceptor} ${f.rfcReceptor}`;
    // Las columnas de fecha filtran por DÍA (el DatePickerInput escribe
    // "YYYY-MM-DD" · igual que Mis solicitudes).
    case "validacion":
      return formatFechaIso(f.fechaValidacion);
    case "timbrado":
      return formatFechaIso(f.fechaTimbrado);
    case "emision":
      return formatFechaIso(f.fechaEmision);
    case "total":
      return f.total.toFixed(2);
    case "moneda":
      return f.moneda;
    case "estatus":
      return badgeEstatusFactura(f.estatus).label;
    case "serie":
      return f.serie ?? "";
    case "folio":
      return f.folio ?? "";
    case "ordenCompra":
      return f.ordenCompraClave ?? "";
    case "tipo":
      return f.tipoComprobante;
    case "observaciones":
      return f.observaciones ?? "";
    case "numDoc":
      return String(f.numeroSolicitud);
    default:
      return "";
  }
}

/**
 * Normalización espejo de los LIKE CI_AI del backend: minúsculas + sin acentos,
 * CONSERVANDO espacios/puntuación (a diferencia de Mis solicitudes, que también
 * quita no-alfanuméricos · aquí el server filtra con SQL y el cliente debe
 * predecir exactamente lo que el server va a devolver).
 */
function normalizarLike(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

/** Filtra por todas las columnas con texto (AND, contains normalizado). */
export function filtrarFacturas(items: Factura[], filtros: FacturaFiltros): Factura[] {
  const activos = Object.entries(filtros)
    .map(([k, v]) => [k as FacturaSortKey, normalizarLike(v ?? "")] as const)
    .filter(([, v]) => v.length > 0);
  if (activos.length === 0) return items;
  return items.filter((f) =>
    activos.every(([key, texto]) => normalizarLike(textoColumna(f, key)).includes(texto)),
  );
}

/**
 * Traducción de los filtros por columna + orden a los query params del backend
 * (paginado server-side): las fechas van como rangos Del=Al del día filtrado;
 * el resto son contains normalizados por columna que el server aplica igual
 * que el cliente. El paginado (page/pageSize) lo agrega la página.
 */
export function aParamsServer(filtros: FacturaFiltros, sort: FacturaSort | null): FacturasParams {
  const out: FacturasParams = {};
  const val = (v: string | undefined) => (v && v.trim().length > 0 ? v.trim() : undefined);

  const validado = val(filtros.validacion);
  if (validado) {
    out.validadoDel = validado;
    out.validadoAl = validado;
  }
  const timbrado = val(filtros.timbrado);
  if (timbrado) {
    out.timbradoDel = timbrado;
    out.timbradoAl = timbrado;
  }
  const emitido = val(filtros.emision);
  if (emitido) {
    out.emitidoDel = emitido;
    out.emitidoAl = emitido;
  }

  out.uuid = val(filtros.uuid);
  out.emisor = val(filtros.emisor);
  out.receptor = val(filtros.receptor);
  out.estatus = val(filtros.estatus);
  out.serie = val(filtros.serie);
  out.folio = val(filtros.folio);
  out.ordenCompra = val(filtros.ordenCompra);
  out.tipo = val(filtros.tipo);
  out.total = val(filtros.total);
  out.moneda = val(filtros.moneda);
  out.observaciones = val(filtros.observaciones);
  out.numDoc = val(filtros.numDoc);

  if (sort) {
    out.sortKey = sort.key;
    out.sortDir = sort.dir;
  }
  return out;
}

/**
 * Params de la PRIMERA página de «Mis facturas» (sin filtros ni orden) · los usa
 * el loader de la ruta para precargar EXACTAMENTE la queryKey que pide la página.
 */
export function paramsInicialesMisFacturas(pageSize: number): FacturasParams {
  return { ...aParamsServer({}, null), page: 1, pageSize };
}
